import PropTypes from "prop-types";
import { useRef } from "react";
import styles from "./ProductTile.css";
import getDealDetails from "../../../getDealDetails";
import ProductDescription from "../../../ProductDescription";
import ProductPrice from "../../../ProductPrice";
import SaveForLater from "../../../SaveForLater";
import Banners from "../../../../Banners";
import ProductLink from "../../../ProductLink";
import ProductBadge from "./ProductBadge";
import classnames from "classnames";
import { withTranslation } from "../../../../translation";
import {
  MIX_MATCH_GROUP,
  MIX_MATCH,
  MORE_COLOURS,
  MORE_COLOURS_FACET
} from "../../../../../state/modules/search/constants";
import getAriaLabelledBy from "../../../getAriaLabelledBy";
import useOnViewBeacon from "../../../UseOnViewBeacon";
import useOnLoadBeacon from "../../../useOnLoadBeacon";
import ProductMediaContainer from "../../../ProductMediaContainer";

export const ProductTile = ({
  id,
  tileId,
  url,
  price,
  isSale,
  isOutlet,
  badgeType,
  productType,
  isSponsored,
  description,
  colourWayId,
  reducedPrice,
  formatCurrency,
  formatTranslation,
  hasMultiplePrices,
  ProductHeroComponent,
  className,
  isSellingFast,
  carouselCatId,
  sellingFastStyle,
  euOmnibusEnabled,
  dealBannerEnabled,
  advertisement: {
    id: advertId,
    onClickBeacon,
    onViewBeacon,
    onLoadBeacon,
    onWishlistBeacon
  },
  sendPlacementOnViewBeacon,
  ...rest
}) => {
  const getProductTileId = id => `product-${id}`;
  const productTileId = getProductTileId(id);
  const labels = getAriaLabelledBy({ productId: productTileId });

  const tileElement = useRef();

  useOnLoadBeacon({ onLoadBeacon });

  useOnViewBeacon({
    targetElement: tileElement.current,
    onViewBeacon,
    sendPlacementOnViewBeacon
  });

  const {
    formattedPrice,
    formattedReducedPrice,
    deal,
    dealAria
  } = getDealDetails({
    price,
    reducedPrice,
    formatCurrency,
    formatTranslation,
    euOmnibusEnabled,
    dealBannerEnabled
  });

  return (
    <article
      ref={tileElement}
      id={getProductTileId(tileId)}
      className={classnames(styles.productTile, className)}
    >
      <ProductLink
        className={styles.productLink}
        href={url}
        productTileId={productTileId}
        carouselCatId={carouselCatId}
        productId={id}
        onClickBeacon={onClickBeacon}
        advertId={advertId}
        {...rest}
      >
        <ProductMediaContainer>
          <ProductHeroComponent {...rest} />
          <Banners
            deal={deal}
            dealAria={dealAria}
            isSponsored={isSponsored}
            isSellingFast={isSellingFast}
            sellingFastStyle={sellingFastStyle}
            ariaLabels={labels}
            badgeType={null}
          />
        </ProductMediaContainer>
        <ProductDescription text={description} labelledById={labels.title} />
        <ProductPrice
          price={formattedPrice}
          isSale={isSale}
          isOutlet={isOutlet}
          reducedPrice={formattedReducedPrice}
          hasMultiplePrices={hasMultiplePrices}
          labelledById={labels.price}
          deal={deal}
          dealAria={dealAria}
          euOmnibusEnabled={euOmnibusEnabled}
          {...rest}
        />
      </ProductLink>
      {productType !== MIX_MATCH_GROUP && (
        <SaveForLater
          productId={id}
          price={price}
          reducedPrice={reducedPrice}
          colourWayId={colourWayId}
          carouselCatId={carouselCatId}
          description={description}
          onWishlistBeacon={onWishlistBeacon}
          {...rest}
        />
      )}
      {badgeType && (
        <ProductBadge type={badgeType} labelledById={labels.productBadge} />
      )}
    </article>
  );
};

ProductTile.propTypes = {
  isSale: PropTypes.bool,
  isOutlet: PropTypes.bool,
  reducedPrice: PropTypes.number,
  id: PropTypes.number.isRequired,
  tileId: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  hasMultiplePrices: PropTypes.bool,
  price: PropTypes.number.isRequired,
  description: PropTypes.string.isRequired,
  colourWayId: PropTypes.number.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  formatTranslation: PropTypes.func.isRequired,
  badgeType: PropTypes.oneOf([MIX_MATCH, MORE_COLOURS, MORE_COLOURS_FACET]),
  productType: PropTypes.string.isRequired,
  ProductHeroComponent: PropTypes.elementType.isRequired,
  isSponsored: PropTypes.bool.isRequired,
  className: PropTypes.string,
  isSellingFast: PropTypes.bool,
  carouselCatId: PropTypes.number,
  sellingFastStyle: PropTypes.string,
  euOmnibusEnabled: PropTypes.bool.isRequired,
  dealBannerEnabled: PropTypes.bool.isRequired,
  advertisement: PropTypes.shape({
    id: PropTypes.number.isRequired,
    onClickBeacon: PropTypes.string.isRequired,
    onViewBeacon: PropTypes.string.isRequired,
    onLoadBeacon: PropTypes.string.isRequired,
    onWishlistBeacon: PropTypes.string.isRequired
  }),
  sendPlacementOnViewBeacon: PropTypes.func
};

export default withTranslation(ProductTile);
